// What the Library of Congress says a book is: its genres and its subjects,
// trimmed to what a page has room for. See docs/loc-enrichment.md for where
// they come from.
//
// Shared rather than worked out by each theme, for the reason shelves.js and
// color.js are: a display switched between themes should not appear to change
// its mind about what a book is about. How the answer is drawn is the theme's.
//
// Either list can be empty, and very often both are - a book with no ISBN, one
// LoC has no record of, one it has not been asked about yet. A theme draws
// nothing for an empty list rather than a placeholder: "no genre" is not a
// fact about the book, only about the catalogue.

export const MAX_GENRES = 3;
export const MAX_SUBJECTS = 4;

const lower = (text) => text.toLowerCase();

function tidy(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const entry of list) {
    const text = typeof entry === 'string' ? entry.trim() : '';
    if (!text || seen.has(lower(text))) continue;
    seen.add(lower(text));
    out.push(text);
  }
  return out;
}

const escape = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Whether `general` appears as a whole word in `specific`: "Fiction" in
// "Science fiction" or "LitRPG (Fiction)", but not "Art" in "Artificial".
function within(general, specific) {
  return new RegExp(`(^|[^a-z])${escape(lower(general))}([^a-z]|$)`).test(lower(specific));
}

export function catalogOf(book) {
  const all = tidy(book?.genres);
  // A genre that a more specific one already says is dropped. A record that
  // reads "Fiction" and "Science fiction" is one fact, and the page has room
  // for three.
  const genres = all.filter((genre) => !all.some((other) => other !== genre && within(genre, other)));
  const named = new Set(all.map(lower));
  // A heading can be filed as both - "History" is a genre and a subject - and
  // saying it twice on one page reads as a mistake.
  const subjects = tidy(book?.subjects).filter((subject) => !named.has(lower(subject)));

  return {
    genres: genres.slice(0, MAX_GENRES),
    subjects: subjects.slice(0, MAX_SUBJECTS),
  };
}
